import { useEffect, useMemo, useRef, useState } from 'react';
import * as GPT from 'gpt-tokenizer';
import { WorldDocument } from '@shared/schema';

export type TokenSource = 'gpt-tokenizer' | 'estimate' | 'idle';

export function selectEncoding(provider?: string, model?: string) {
  const p = provider?.toLowerCase() || '';
  const m = model?.toLowerCase() || '';
  if (p.includes('openai')) {
    if (/(4o|o3|o1|4o-mini|4o\-|mini|omni)/.test(m)) return 'o200k_base';
    return 'cl100k_base';
  }
  // Default to cl100k_base for unknown providers
  return 'cl100k_base';
}

export function useTokenCount(text: string | undefined, provider?: string, model?: string, debounceMs = 200) {
  const [tokens, setTokens] = useState<number>(0);
  const [source, setSource] = useState<TokenSource>('idle');
  const [loading, setLoading] = useState(false);

  const normalized = useMemo(() => (text ?? '').trim(), [text]);
  const encoding = useMemo(() => selectEncoding(provider, model), [provider, model]);
  const previousInputsRef = useRef<{ normalized: string; encoding: string }>({ normalized: '', encoding: '' });
  const lastResultRef = useRef<{ tokens: number; source: TokenSource }>({ tokens: 0, source: 'idle' });

  useEffect(() => {
    if (!normalized) {
      previousInputsRef.current = { normalized: '', encoding };
      lastResultRef.current = { tokens: 0, source: 'idle' };
      setTokens(0);
      setSource('idle');
      setLoading(false);
      return;
    }

    if (
      previousInputsRef.current.normalized === normalized &&
      previousInputsRef.current.encoding === encoding
    ) {
      return;
    }

    previousInputsRef.current = { normalized, encoding };

    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        let ids: number[] = [];
        try {
          const encObj = (GPT as any)[encoding] || (GPT as any).cl100k_base || (GPT as any);
          if (encObj && typeof encObj.encode === 'function') {
            try {
              ids = encObj.encode(normalized);
            } catch {
              ids = [];
            }
          }
          if ((!ids || ids.length === 0) && typeof (GPT as any).encode === 'function') {
            try { ids = (GPT as any).encode(normalized, encoding); } catch { try { ids = (GPT as any).encode(normalized); } catch { ids = []; } }
          }
        } catch {
          ids = [];
        }
        if (!cancelled) {
          const nextTokens = Array.isArray(ids) && ids.length > 0 ? ids.length : Math.ceil(normalized.length / 4);
          const nextSource: TokenSource = Array.isArray(ids) && ids.length > 0 ? 'gpt-tokenizer' : 'estimate';
          const prevResult = lastResultRef.current;
          if (prevResult.tokens !== nextTokens) {
            setTokens(nextTokens);
          }
          if (prevResult.source !== nextSource) {
            setSource(nextSource);
          }
          lastResultRef.current = { tokens: nextTokens, source: nextSource };
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [normalized, encoding, debounceMs]);

  const result = useMemo(() => ({ tokens, source, loading }) as const, [tokens, source, loading]);
  return result;
}

/**
 * Enhanced token counting hook that includes world document content
 */
export function useTokenCountWithDocument(
  text: string | undefined,
  worldDocument: WorldDocument | undefined,
  provider?: string,
  model?: string,
  debounceMs = 200
) {
  const [tokens, setTokens] = useState<number>(0);
  const [breakdown, setBreakdown] = useState<{
    text: number;
    document: number;
    total: number;
  }>({ text: 0, document: 0, total: 0 });
  const [source, setSource] = useState<TokenSource>('idle');
  const [loading, setLoading] = useState(false);
  const [warning, setWarning] = useState<string | undefined>();

  const normalizedText = useMemo(() => (text ?? '').trim(), [text]);
  const documentContent = useMemo(() => worldDocument?.content?.trim() ?? '', [worldDocument?.content]);
  const combinedContent = useMemo(() => {
    if (!normalizedText && !documentContent) return '';
    if (!documentContent) return normalizedText;
    if (!normalizedText) return documentContent;
    return `${normalizedText}\n\n--- World Context ---\n${documentContent}`;
  }, [normalizedText, documentContent]);
  
  const encoding = useMemo(() => selectEncoding(provider, model), [provider, model]);
  const previousInputsRef = useRef<{ combined: string; encoding: string }>({ combined: '', encoding: '' });
  const lastResultRef = useRef<{ tokens: number; text: number; document: number; source: TokenSource }>({
    tokens: 0,
    text: 0,
    document: 0,
    source: 'idle',
  });

  // Provider-specific token limits
  const providerLimits = useMemo(() => {
    const p = provider?.toLowerCase() || '';
    switch (p) {
      case 'openai':
        return { limit: 128000, warningThreshold: 0.8 }; // GPT-4 context window
      case 'gemini':
        return { limit: 1000000, warningThreshold: 0.8 }; // Gemini 1.5 Pro context window
      case 'openrouter':
        return { limit: 32768, warningThreshold: 0.8 }; // Conservative default
      default:
        return { limit: 8000, warningThreshold: 0.8 }; // Conservative fallback
    }
  }, [provider]);

  useEffect(() => {
    if (!combinedContent) {
      previousInputsRef.current = { combined: '', encoding };
      lastResultRef.current = { tokens: 0, text: 0, document: 0, source: 'idle' };
      setTokens(0);
      setBreakdown({ text: 0, document: 0, total: 0 });
      setSource('idle');
      setWarning(undefined);
      setLoading(false);
      return;
    }

    if (
      previousInputsRef.current.combined === combinedContent &&
      previousInputsRef.current.encoding === encoding
    ) {
      return;
    }

    previousInputsRef.current = { combined: combinedContent, encoding };

    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading((prev) => (prev ? prev : true));
      try {
        // Count tokens for individual parts
        const textTokens = await countTokensForText(normalizedText, encoding);
        const documentTokens = await countTokensForText(documentContent, encoding);
        const totalTokens = await countTokensForText(combinedContent, encoding);

        if (!cancelled) {
          const prevResult = lastResultRef.current;
          if (prevResult.tokens !== totalTokens.count) {
            setTokens(totalTokens.count);
          }
          if (
            prevResult.text !== textTokens.count ||
            prevResult.document !== documentTokens.count ||
            prevResult.tokens !== totalTokens.count
          ) {
            setBreakdown({
              text: textTokens.count,
              document: documentTokens.count,
              total: totalTokens.count,
            });
          }
          if (prevResult.source !== totalTokens.source) {
            setSource(totalTokens.source);
          }
          lastResultRef.current = {
            tokens: totalTokens.count,
            text: textTokens.count,
            document: documentTokens.count,
            source: totalTokens.source,
          };

          // Check for warnings
          const warningThreshold = providerLimits.limit * providerLimits.warningThreshold;
          if (totalTokens.count >= providerLimits.limit) {
            setWarning(`Content exceeds ${provider || 'provider'} limit (${totalTokens.count}/${providerLimits.limit} tokens). Consider reducing document size.`);
          } else if (totalTokens.count >= warningThreshold) {
            setWarning(`Content approaching ${provider || 'provider'} limit (${totalTokens.count}/${providerLimits.limit} tokens). Consider optimizing content.`);
          } else {
            setWarning(undefined);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, debounceMs);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [combinedContent, normalizedText, documentContent, encoding, debounceMs, provider, providerLimits]);

  const exceedsLimit = tokens >= providerLimits.limit;
  const nearLimit = tokens >= providerLimits.limit * providerLimits.warningThreshold;
  const result = useMemo(
    () => ({
      tokens,
      breakdown,
      source,
      loading,
      warning,
      exceedsLimit,
      nearLimit,
    }) as const,
    [tokens, breakdown, source, loading, warning, exceedsLimit, nearLimit],
  );

  return result;
}

/**
 * Helper function to count tokens for a given text
 */
export async function countTokensForText(text: string, encoding: string): Promise<{ count: number; source: TokenSource }> {
  if (!text || text.trim().length === 0) {
    return { count: 0, source: 'idle' };
  }

  try {
    let ids: number[] = [];
    try {
      const encObj = (GPT as any)[encoding] || (GPT as any).cl100k_base || (GPT as any);
      if (encObj && typeof encObj.encode === 'function') {
        try {
          ids = encObj.encode(text);
        } catch {
          ids = [];
        }
      }
      if ((!ids || ids.length === 0) && typeof (GPT as any).encode === 'function') {
        try { 
          ids = (GPT as any).encode(text, encoding); 
        } catch { 
          try { 
            ids = (GPT as any).encode(text); 
          } catch { 
            ids = []; 
          } 
        }
      }
    } catch {
      ids = [];
    }

    if (Array.isArray(ids) && ids.length > 0) {
      return { count: ids.length, source: 'gpt-tokenizer' };
    } else {
      return { count: Math.ceil(text.length / 4), source: 'estimate' };
    }
  } catch {
    return { count: Math.ceil(text.length / 4), source: 'estimate' };
  }
}

/**
 * Hook for getting provider-specific token limits
 */
export function useProviderTokenLimits(provider?: string) {
  return useMemo(() => {
    const p = provider?.toLowerCase() || '';
    switch (p) {
      case 'openai':
        return { 
          limit: 128000, 
          warningThreshold: 102400, // 80% of limit
          name: 'OpenAI GPT-4'
        };
      case 'gemini':
        return { 
          limit: 1000000, 
          warningThreshold: 800000, // 80% of limit
          name: 'Google Gemini'
        };
      case 'openrouter':
        return { 
          limit: 32768, 
          warningThreshold: 26214, // 80% of limit
          name: 'OpenRouter'
        };
      default:
        return { 
          limit: 8000, 
          warningThreshold: 6400, // 80% of limit
          name: 'Unknown Provider'
        };
    }
  }, [provider]);
}
